import { getCookie, setCookie } from 'cookies-next';
import { CustomQueryStringParameterItems } from 'libs/contentful/types';
import { createCookieOptions, CUSTOM_QSP_COOKIE } from './cookies';

interface PersistableCustomQueryStringParameterItem extends Omit<CustomQueryStringParameterItems, 'key' | 'value'> {
  key?: string | null;
  value?: string | null;
  paramKey?: string | null;
  paramValue?: string | null;
}

function isPersistableAttributionKey(key: string): boolean {
  return key.startsWith('landing_page_origin');
}

// Contentful items use key/value, while resolved QSP items use paramKey/paramValue
function resolveKey(item: PersistableCustomQueryStringParameterItem): string | undefined {
  return (item.key || item.paramKey) ?? undefined;
}

function resolveValue(item: PersistableCustomQueryStringParameterItem): string | undefined {
  if (item.value !== undefined && item.value !== null) {
    return String(item.value);
  }
  if (item.paramValue !== undefined && item.paramValue !== null) {
    return String(item.paramValue);
  }
  return undefined;
}

function serializeParams(params: Record<string, string>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (key && value) {
      searchParams.set(key, value);
    }
  });
  return searchParams.toString();
}

function deserializeParams(queryString: string): Record<string, string> {
  if (!queryString) {
    return {};
  }

  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(queryString);
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  // An accumulator returned at its boundary; callers narrow what they read from it.
  // oxlint-disable-next-line anti-slop/no-known-value-widening
  return params;
}

function filterAttributionKeys(params: Record<string, string>): Record<string, string> {
  return Object.entries(params).reduce(
    (persistedAttribution, [key, value]) => {
      if (isPersistableAttributionKey(key) && value) {
        persistedAttribution[key] = value;
      }
      return persistedAttribution;
    },
    {} as Record<string, string>,
  );
}

export function extractAttributionFromItems(
  items: PersistableCustomQueryStringParameterItem[] | undefined,
): Record<string, string> {
  if (!items || items.length === 0) {
    return {};
  }

  return items.reduce(
    (persistableParams, item) => {
      const key = resolveKey(item);
      const value = resolveValue(item);
      if (!key || !value || !isPersistableAttributionKey(key)) {
        return persistableParams;
      }

      persistableParams[key] = value;
      return persistableParams;
    },
    {} as Record<string, string>,
  );
}

export function setAttributionCookie(params: Record<string, string>): void {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof window === 'undefined') {
    return;
  }

  if (!params || Object.keys(params).length === 0) {
    return;
  }

  const serialized = serializeParams(params);
  if (!serialized) {
    return;
  }

  setCookie(
    CUSTOM_QSP_COOKIE,
    serialized,
    createCookieOptions({
      enableCrossSubdomain: true,
      cookieOptions: { maxAge: undefined },
    }),
  );
}

export function getPersistedAttribution(): Record<string, string> {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof window === 'undefined') {
    return {};
  }

  const cookieValue = getCookie(CUSTOM_QSP_COOKIE);
  if (!cookieValue) {
    return {};
  }

  return getPersistedAttributionFromSerializedValue(String(cookieValue));
}

export function getPersistedAttributionFromSerializedValue(cookieValue: string | undefined): Record<string, string> {
  if (!cookieValue) {
    return {};
  }

  const deserializedCookie = deserializeParams(cookieValue);
  return filterAttributionKeys(deserializedCookie);
}
